import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArchive, faSearch, faBuilding, faCalendarAlt, faChevronLeft, faChevronRight, faUser } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import ConfirmModal from './components/ConfirmModal';

const DEPT_STYLE = {
  define:    { bg: '#dbeafe', color: '#1e40af', label: 'Define' },
  voa:       { bg: '#fef9c3', color: '#a16207', label: 'Voa' },
  immersion: { bg: '#fee2e2', color: '#991b1b', label: 'Immersion' },
};

const TYPE_LABELS = {
  contrat: 'Contrat', appel_offre: 'Appel d\'Offre', consultation_restreinte: 'Consultation Restreinte',
};

const STEP_LABELS = {
  ao: 'Avant-Vente', setup: 'Setup', terrain: 'Terrain', reporting: 'Reporting', closing: 'Closing',
};

const PER_PAGE = 15;

const ProjectArchive = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedSearch(search); setCurrentPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const params = { archived: 1, page: currentPage, per_page: PER_PAGE };
      if (debouncedSearch) params.search = debouncedSearch;
      if (department) params.department = department;
      const res = await api.get('/projects', { params });
      setProjects(res.data.data || []);
      setTotalPages(res.data.last_page || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, department]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= maxVisible; i++) pages.push(i);
    } else if (currentPage >= totalPages - 2) {
      for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = currentPage - 2; i <= currentPage + 2; i++) pages.push(i);
    }
    return pages;
  };

  return (
    <div style={{ padding: '20px 28px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FontAwesomeIcon icon={faArchive} style={{ color: '#6b7280', fontSize: '1.1rem' }} />
          <h4 style={{ margin: 0, fontWeight: 700, fontSize: '1.15rem', color: '#111827' }}>
            Projets Archivés
          </h4>
          {!loading && (
            <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#9ca3af', background: '#f3f4f6', padding: '2px 10px', borderRadius: 10 }}>
              {total}
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#d1d5db', fontSize: '0.75rem' }} />
          <input
            type="text"
            placeholder="Rechercher un projet ou client..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: '0.78rem', outline: 'none' }}
            onFocus={e => e.target.style.borderColor = '#9ca3af'}
            onBlur={e => e.target.style.borderColor = '#e5e7eb'}
          />
        </div>
        <select
          value={department}
          onChange={e => { setDepartment(e.target.value); setCurrentPage(1); }}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: '0.78rem', color: '#374151', background: '#fff', cursor: 'pointer' }}
        >
          <option value="">Tous les départements</option>
          <option value="define">Define</option>
          <option value="voa">Voa</option>
          <option value="immersion">Immersion</option>
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
        </div>
      ) : projects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <FontAwesomeIcon icon={faArchive} style={{ fontSize: '2rem', color: '#e5e7eb', marginBottom: 12 }} />
          <div style={{ fontSize: '0.88rem', color: '#9ca3af' }}>Aucun projet archivé</div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {projects.map(p => {
              const dept = DEPT_STYLE[p.department] || DEPT_STYLE.immersion;
              const archivedDate = p.archived_at ? new Date(p.archived_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

              return (
                <div key={p.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                    cursor: 'pointer', transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#9ca3af'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}
                  onClick={() => navigate(`/projects/${p.id}`)}
                >
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                      </span>
                      <span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '1px 8px', borderRadius: 8, background: dept.bg, color: dept.color, flexShrink: 0 }}>
                        {dept.label}
                      </span>
                      <span style={{ fontSize: '0.62rem', fontWeight: 500, padding: '1px 8px', borderRadius: 8, border: '1px solid #e5e7eb', color: '#6b7280', flexShrink: 0 }}>
                        {TYPE_LABELS[p.project_type]}
                      </span>
                      {p.ao_result === 'lost' && (
                        <span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '1px 8px', borderRadius: 8, background: '#fee2e2', color: '#991b1b', flexShrink: 0 }}>
                          {p.project_type === 'consultation_restreinte' ? 'Non retenue' : 'Perdu'}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: '0.7rem', color: '#9ca3af' }}>
                      {p.client && (
                        <span><FontAwesomeIcon icon={faBuilding} size="xs" className="me-1" />{p.client.name}</span>
                      )}
                      {(p.assignees?.length > 0) && (
                        <span><FontAwesomeIcon icon={faUser} size="xs" className="me-1" />{p.assignees.map(a => a.name).join(', ')}</span>
                      )}
                      {archivedDate && (
                        <span><FontAwesomeIcon icon={faCalendarAlt} size="xs" className="me-1" />Archivé le {archivedDate}</span>
                      )}
                    </div>
                  </div>

                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 gap-4">
              <div className="flex items-center text-sm text-gray-700">
                <span>
                  Affichage de {((currentPage - 1) * PER_PAGE) + 1} à {Math.min(currentPage * PER_PAGE, total)} sur {total} résultats
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3">
                {/* Mobile navigation */}
                <div className="flex sm:hidden space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <div className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium">
                    {currentPage} / {totalPages}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>

                {/* Desktop navigation */}
                <div className="hidden sm:flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>

                  <div className="flex space-x-1">
                    {getPageNumbers().map(pageNum => (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? 'bg-red-600 text-white shadow-md'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={errorModal.open}
        onClose={() => setErrorModal({ open: false, message: '' })}
        title="Erreur"
        message={errorModal.message}
        variant="danger"
      />
    </div>
  );
};

export default ProjectArchive;
